import React, { useState } from 'react';
import { parsePostmanCollection } from '../../utils/postmanParser';

const PostmanImporter = ({ onImport }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [collection, setCollection] = useState(null);
    const [requests, setRequests] = useState([]);
    const [selectedRequestId, setSelectedRequestId] = useState(null);
    const [error, setError] = useState('');
    const [warning, setWarning] = useState('');

    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setError('');
        setWarning('');
        setSelectedFile(file);
        setCollection(null);
        setRequests([]);
        setSelectedRequestId(null);

        // Validate file type
        if (!file.name.endsWith('.json')) {
            setError('Please select a valid JSON file');
            return;
        }

        try {
            // Read file content
            const fileContent = await readFileAsText(file);

            // Parse JSON
            let jsonData;
            try {
                jsonData = JSON.parse(fileContent);
            } catch (parseError) {
                setError('Invalid JSON file. Please upload a valid Postman collection.');
                return;
            }

            // Parse Postman collection
            const parsed = parsePostmanCollection(jsonData);

            setCollection({
                name: parsed.collectionName,
                version: parsed.version,
            });
            setRequests(parsed.requests);

            // Show warning for unknown versions
            if (parsed.version === 'unknown') {
                setWarning('Collection version could not be determined. Import may not work correctly.');
            }
        } catch (err) {
            setError(err.message || 'Failed to parse Postman collection');
        }
    };

    const readFileAsText = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = (e) => reject(new Error('Failed to read file'));
            reader.readAsText(file);
        });
    };

    const handleRequestSelect = (requestId) => {
        setSelectedRequestId(requestId);
        setWarning('');
    };

    const handleImport = () => {
        const request = requests.find(r => r.id === selectedRequestId);
        if (!request) return;

        // Check for unsupported auth
        if (request.auth && request.auth.type === 'unsupported') {
            setWarning(`⚠️ ${request.auth.message}`);
        }

        // Prepare import data
        const importData = {
            url: request.url,
            method: request.method,
            headers: request.headers,
            body: request.body,
        };

        // Add auth if supported
        if (request.auth) {
            if (request.auth.type === 'bearer') {
                importData.authType = 'bearer';
                importData.authToken = request.auth.token;
            } else if (request.auth.type === 'header') {
                importData.authType = 'header';
                importData.authKey = request.auth.key;
                importData.authValue = request.auth.value;
            }
        }

        onImport(importData);

        // Reset state
        setSelectedFile(null);
        setCollection(null);
        setRequests([]);
        setSelectedRequestId(null);
    };

    const selectedRequest = requests.find(r => r.id === selectedRequestId);

    return (
        <div>
            <style>{`
        .postman-import-title {
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 12px;
          color: var(--text-primary);
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        
        .postman-import-description {
          color: var(--text-secondary);
          margin-bottom: 16px;
          font-size: 14px;
        }
        
        .file-input-wrapper {
          position: relative;
          display: inline-block;
          width: 100%;
          margin-bottom: 16px;
        }
        
        .file-input-wrapper input[type="file"] {
          position: absolute;
          left: -9999px;
        }
        
        .file-input-label {
          display: inline-block;
          padding: 10px 20px;
          background: var(--light-gray);
          border: 2px dashed var(--border-color);
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          width: 100%;
          text-align: center;
          font-size: 14px;
          color: var(--text-primary);
        }
        
        .file-input-label:hover {
          border-color: var(--text-secondary);
          background: var(--card-bg);
        }
        
        .file-selected {
          font-size: 13px;
          color: var(--text-secondary);
          margin-top: 8px;
        }
        
        .collection-info {
          background: var(--light-gray);
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 16px;
        }
        
        .collection-name {
          font-weight: 600;
          font-size: 16px;
          color: var(--text-primary);
          margin-bottom: 4px;
        }
        
        .collection-meta {
          font-size: 13px;
          color: var(--text-secondary);
        }
        
        .request-list {
          max-height: 300px;
          overflow-y: auto;
          border: 1px solid var(--border-color);
          border-radius: 8px;
          margin-bottom: 16px;
        }
        
        .request-item {
          padding: 12px 16px;
          border-bottom: 1px solid var(--border-color);
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .request-item:last-child {
          border-bottom: none;
        }
        
        .request-item:hover {
          background: var(--light-gray);
        }
        
        .request-item.selected {
          background: rgba(90, 169, 230, 0.1);
          border-left: 3px solid #5aa9e6;
        }
        
        .request-item-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 4px;
        }
        
        .request-method {
          font-weight: 700;
          font-size: 11px;
          padding: 2px 8px;
          border-radius: 4px;
          text-transform: uppercase;
        }
        
        .method-GET { background: #61AFFE; color: white; }
        .method-POST { background: #49CC90; color: white; }
        .method-PUT { background: #FCA130; color: white; }
        .method-PATCH { background: #50E3C2; color: white; }
        .method-DELETE { background: #F93E3E; color: white; }
        
        .request-name {
          font-weight: 600;
          font-size: 14px;
          color: var(--text-primary);
        }
        
        .request-folder {
          font-size: 12px;
          color: var(--text-secondary);
          margin-bottom: 4px;
        }
        
        .request-url {
          font-size: 12px;
          color: var(--text-secondary);
          font-family: monospace;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        .request-preview {
          background: var(--light-gray);
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 16px;
        }
        
        .preview-section {
          margin-bottom: 12px;
        }
        
        .preview-section:last-child {
          margin-bottom: 0;
        }
        
        .preview-label {
          font-weight: 600;
          font-size: 12px;
          color: var(--text-secondary);
          margin-bottom: 4px;
          text-transform: uppercase;
        }
        
        .preview-value {
          font-size: 13px;
          color: var(--text-primary);
          font-family: monospace;
          background: var(--card-bg);
          padding: 8px;
          border-radius: 4px;
          word-break: break-all;
        }
        
        .preview-json {
          font-size: 12px;
          font-family: monospace;
          background: var(--card-bg);
          padding: 12px;
          border-radius: 4px;
          max-height: 150px;
          overflow-y: auto;
          white-space: pre-wrap;
          word-break: break-word;
        }
        
        .warning-box {
          background: rgba(249, 168, 37, 0.1);
          border: 1px solid rgba(249, 168, 37, 0.3);
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 16px;
          font-size: 13px;
          color: var(--text-primary);
        }
        
        .import-actions {
          display: flex;
          gap: 10px;
        }
        
        @media (max-width: 768px) {
          .import-actions {
            flex-direction: column;
          }
          
          .import-actions button {
            width: 100%;
          }
        }
      `}</style>

            <h3 className="postman-import-title">Import from Postman Collection</h3>
            <p className="postman-import-description">
                Upload a Postman collection JSON file to import API requests
            </p>

            {error && (
                <div className="error-message" style={{ marginBottom: '15px' }}>
                    <span>⚠️</span>
                    <span>{error}</span>
                </div>
            )}

            {warning && (
                <div className="warning-box">
                    {warning}
                </div>
            )}

            {/* File Upload */}
            <div className="file-input-wrapper">
                <input
                    type="file"
                    id="postman-file"
                    accept=".json"
                    onChange={handleFileSelect}
                />
                <label htmlFor="postman-file" className="file-input-label">
                    📁 {selectedFile ? selectedFile.name : 'Choose Postman Collection (.json)'}
                </label>
            </div>

            {/* Collection Info */}
            {collection && (
                <div className="collection-info">
                    <div className="collection-name">📦 {collection.name}</div>
                    <div className="collection-meta">
                        Version: {collection.version} • {requests.length} request{requests.length !== 1 ? 's' : ''} found
                    </div>
                </div>
            )}

            {/* Request List */}
            {requests.length > 0 && (
                <>
                    <div className="request-list">
                        {requests.map((request) => (
                            <div
                                key={request.id}
                                className={`request-item ${selectedRequestId === request.id ? 'selected' : ''}`}
                                onClick={() => handleRequestSelect(request.id)}
                            >
                                <div className="request-item-header">
                                    <span className={`request-method method-${request.method}`}>
                                        {request.method}
                                    </span>
                                    <span className="request-name">{request.name}</span>
                                </div>
                                {request.folder && (
                                    <div className="request-folder">📂 {request.folder}</div>
                                )}
                                <div className="request-url">{request.url}</div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* Request Preview */}
            {selectedRequest && (
                <div className="request-preview">
                    <div className="preview-section">
                        <div className="preview-label">URL</div>
                        <div className="preview-value">{selectedRequest.url}</div>
                    </div>

                    {Object.keys(selectedRequest.headers).length > 0 && (
                        <div className="preview-section">
                            <div className="preview-label">Headers</div>
                            <div className="preview-json">
                                {JSON.stringify(selectedRequest.headers, null, 2)}
                            </div>
                        </div>
                    )}

                    {selectedRequest.body && (
                        <div className="preview-section">
                            <div className="preview-label">Body</div>
                            <div className="preview-json">
                                {selectedRequest.body}
                            </div>
                        </div>
                    )}

                    {selectedRequest.auth && (
                        <div className="preview-section">
                            <div className="preview-label">Authentication</div>
                            <div className="preview-value">
                                {selectedRequest.auth.type === 'bearer' && `Bearer Token (${selectedRequest.auth._original})`}
                                {selectedRequest.auth.type === 'header' && `Custom Header: ${selectedRequest.auth.key} (${selectedRequest.auth._original})`}
                                {selectedRequest.auth.type === 'unsupported' && `⚠️ Unsupported (${selectedRequest.auth._original})`}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Import Actions */}
            {selectedRequest && (
                <div className="import-actions">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleImport}
                    >
                        Import Selected Request
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                            setSelectedFile(null);
                            setCollection(null);
                            setRequests([]);
                            setSelectedRequestId(null);
                            setError('');
                            setWarning('');
                        }}
                    >
                        Cancel
                    </button>
                </div>
            )}
        </div>
    );
};

export default PostmanImporter;
